import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { NavigateFunction, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Pagination } from '@mui/material';
import _ from 'lodash';
import { IColumnType } from 'components/Table/types';
import { PageContentContainer, PageContentFilter, PageContentTable } from '../PageContentContainer';
import { useDebounce } from 'hooks/useDebounce';
import { filterByFields } from '../../utils/global.util';
import { DataFetchKeys, useDataFetchContext } from '../../context/DataFetchContext';
import {
  PatientTableColumn,
  PatientId,
  PatientDefaultSort,
  PatientTableColumnTitles,
  IPatient,
  PatientStatus
} from 'constants/patients.constants';
import { pagesEnum } from 'constants/ui.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import CircularIndeterminate from 'components/Loading';
import NoInfo from 'components/NoInfo';
import { userStatuses } from 'constants/user.constants';
import SelectBox from 'components/SelectBox';
import BasicTable from 'components/Table';

const ITEMS_PER_PAGE = 100;

const createActionsCells = (navigate: NavigateFunction) => [
  {
    element: (
      <Button color='secondary' sx={{ marginRight: '46px', textTransform: 'none', fontWeight: 'bold' }} variant='text'>
        Info
      </Button>
    ),
    onClick: (patient: IPatient) => {
      navigate(`/${pagesEnum.PATIENTS}/${patient.guid}`);
    }
  }
];

const createPatientsColumns = (): IColumnType[] => {
  return Object.keys(PatientTableColumn).map((element) => ({
    fieldId: PatientId,
    name: element,
    title: PatientTableColumnTitles[element as keyof typeof PatientTableColumnTitles] || element,
    type: PatientTableColumn[element],
    customValueHandle: (val, rec) =>
      rec.active ? (rec.siteToTransferTo ? PatientStatus.In_Transfer : PatientStatus.Active) : PatientStatus.Inactive
  }));
};

const PatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState(1);
  const { siteId } = useParams();
  const { patients, loading, error } = useAppSelector((state) => state.patients);
  const { loadDataByKeys } = useDataFetchContext();
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>();
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedValue = useDebounce(searchValue);

  const actionsCells = useMemo(() => createActionsCells(navigate), [navigate]);
  const patientsColumns = useMemo(() => createPatientsColumns(), []);

  const parsedSitesOptions = useMemo(
    () => _.uniq(_.map(patients, (patient) => patient?.assignedSite)).filter(Boolean),
    [patients]
  );

  const parsedCountriesOptions = useMemo(
    () => _.uniq(_.map(patients, (patient) => patient?.country)).filter(Boolean),
    [patients]
  );

  const updateTableData = useCallback(() => {
    if (!patients || !filterValues?.country) return [];

    return patients.filter((patient: IPatient) => {
      const isStatusMatch =
        !filterValues.status.value ||
        filterValues.status.value === 'All' ||
        (filterValues.status.value === PatientStatus.Active) === patient.active;

      const isCountryMatch =
        filterValues.country.value.includes('All') || filterValues.country.value.includes(patient.country);

      const isSiteMatch =
        filterValues.sites.value.includes('All') || filterValues.sites.value.includes(patient.assignedSite);

      return isStatusMatch && isCountryMatch && isSiteMatch;
    });
  }, [patients, filterValues]);

  const filteredPatientsData = useMemo(
    () =>
      filterByFields(
        updateTableData(),
        debouncedValue,
        ['identityNumber', 'firstName', 'lastName', 'country', 'assignedSite'],
        [['firstName', 'lastName']]
      ),
    [updateTableData, debouncedValue]
  );

  const paginatedData = useMemo(() => {
    const startIndex = (pageNumber - 1) * ITEMS_PER_PAGE;
    return filteredPatientsData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredPatientsData, pageNumber]);

  const totalPages = Math.ceil((filteredPatientsData?.length || 0) / ITEMS_PER_PAGE);

  const onFilterChange = (filterType: string, filterValue: string | string[]) => {
    const updatedFilterValues = {
      ...filterValues,
      [filterType]: { ...filterValues?.[filterType], value: filterValue }
    };
    setFilterValues(updatedFilterValues);

    const newSearchParams = new URLSearchParams(searchParams);
    if (Array.isArray(filterValue)) {
      newSearchParams.delete(filterType);
      filterValue.forEach((value) => newSearchParams.append(filterType, value));
    } else {
      newSearchParams.set(filterType, filterValue);
    }
    setSearchParams(newSearchParams);
  };

  const handlePaginationChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
    setPageNumber(value);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const onResetSelections = () => {
    const resetFilters = {
      country: {
        displayName: 'Country',
        options: parsedCountriesOptions,
        value: ['All'],
        multiple: true
      },
      sites: {
        displayName: 'Sites',
        options: parsedSitesOptions,
        value: ['All'],
        multiple: true
      },
      status: {
        displayName: 'Status',
        options: userStatuses,
        value: 'All'
      }
    };
    setFilterValues(resetFilters);

    const newSearchParams = new URLSearchParams();
    newSearchParams.append('country', 'All');
    newSearchParams.append('sites', 'All');
    newSearchParams.append('status', 'All');
    setSearchParams(newSearchParams);

    navigate(`/${pagesEnum.PATIENTS}`, { replace: true });
  };

  useEffect(() => {
    if (!patients?.length) {
      loadDataByKeys([DataFetchKeys.PATIENTS]);
    }
  }, [patients?.length]);

  useEffect(() => {
    const initialFilters = {
      sites: {
        displayName: 'Sites',
        options: parsedSitesOptions,
        value: searchParams.getAll('sites').length > 0 ? searchParams.getAll('sites') : ['All'],
        multiple: true
      },
      country: {
        displayName: 'Country',
        options: parsedCountriesOptions,
        value: searchParams.getAll('country').length > 0 ? searchParams.getAll('country') : ['All'],
        multiple: true
      },
      status: {
        displayName: 'Status',
        options: userStatuses,
        value: searchParams.get('status') || 'All'
      }
    };
    setFilterValues(initialFilters);
  }, [parsedSitesOptions, parsedCountriesOptions, searchParams]);

  useEffect(() => {
    if (siteId && filterValues?.sites) {
      const site = patients?.find((patient) => patient?.siteId === Number(siteId));
      if (site) {
        if (filterValues.sites.value !== site.assignedSite) {
          onFilterChange('sites', site.assignedSite!);
        }
      } else {
        navigate(`/${pagesEnum.PATIENTS}`, { replace: true });
      }
    }
  }, [siteId, filterValues, patients]);

  const paginationComponent = useMemo(
    () => <Pagination count={totalPages} page={pageNumber} onChange={handlePaginationChange} />,
    [totalPages, pageNumber, handlePaginationChange]
  );

  if (error) return <NoInfo />;
  if (loading) return <CircularIndeterminate />;

  return (
    <PageContentContainer
      pageName='Patients'
      searchValue={searchValue}
      filteredResultsCount={filteredPatientsData?.length || 0}
      resetFilterClicked={onResetSelections}
      searchValueChanged={handleInputChange}
    >
      <PageContentFilter>
        {_.map(_.keys(filterValues), (filterValuesKey) => (
          <SelectBox
            key={filterValuesKey}
            items={filterValues?.[filterValuesKey].options}
            values={filterValues?.[filterValuesKey].value}
            label={filterValues?.[filterValuesKey].displayName}
            inputLabel={filterValues?.[filterValuesKey].displayName}
            type={filterValuesKey}
            onFilterChange={onFilterChange}
            withAllOption
            multiple={filterValues?.[filterValuesKey].multiple}
          />
        ))}
      </PageContentFilter>
      <PageContentTable>
        <BasicTable
          fieldId='id'
          data={paginatedData}
          columns={patientsColumns}
          defaultSortBy={PatientDefaultSort}
          actionsCell={actionsCells}
        >
          {paginationComponent}
        </BasicTable>
      </PageContentTable>
    </PageContentContainer>
  );
};

export default PatientsPage;
